import { useEffect, useMemo, useState } from "react";
import { FaBell, FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import client from "../api/client";
import "./Notifications.css";

const API_LIMIT = 100;

const formatDateTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { date: "--", time: "--" };
  return {
    date: date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }),
    time: date.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" }),
  };
};

export default function Notifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [markingId, setMarkingId] = useState(null);

  const loadNotifications = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await client.get("/admin-notifications", { params: { limit: API_LIMIT } });
      setNotifications(Array.isArray(data?.notifications) ? data.notifications : []);
    } catch (requestError) {
      console.error("Load notification history failed:", requestError);
      setError(requestError.response?.data?.message || "Notifications could not be loaded. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void loadNotifications(); }, []);

  const filteredNotifications = useMemo(() => notifications.filter((notification) => (
    filter === "all" || (filter === "unread" ? !notification.read : notification.read)
  )), [filter, notifications]);

  const counts = useMemo(() => ({
    all: notifications.length,
    unread: notifications.filter((notification) => !notification.read).length,
    read: notifications.filter((notification) => notification.read).length,
  }), [notifications]);

  const openNotification = async (notification) => {
    if (!notification.read) {
      setMarkingId(notification.id);
      try {
        await client.patch(`/admin-notifications/${notification.id}/read`);
        setNotifications((items) => items.map((item) => (
          item.id === notification.id ? { ...item, read: true, read_at: new Date().toISOString() } : item
        )));
      } catch (requestError) {
        console.error("Mark notification read failed:", requestError);
      } finally {
        setMarkingId(null);
      }
    }

    // Stored destinations are server-generated; only allow the supported internal route.
    if (String(notification.destination_path || "").startsWith("/help-desk")) navigate(notification.destination_path);
  };

  return (
    <main className="notifications-page">
      <header className="notifications-page-header">
        <div>
          <span className="notifications-eyebrow">Admin centre</span>
          <h1>Notifications</h1>
          <p>Review Help Desk activity and open a notification to view its related request.</p>
        </div>
        <button type="button" className="notifications-refresh" onClick={loadNotifications} disabled={loading}>
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </header>

      <section className="notifications-card" aria-label="Notification history">
        <div className="notifications-toolbar">
          <div className="notifications-filters" role="tablist" aria-label="Notification status">
            {["all", "unread", "read"].map((name) => (
              <button key={name} type="button" role="tab" aria-selected={filter === name} className={filter === name ? "active" : ""} onClick={() => setFilter(name)}>
                {name[0].toUpperCase() + name.slice(1)} <span>{counts[name]}</span>
              </button>
            ))}
          </div>
          <span className="notifications-total">{counts.unread} unread</span>
        </div>

        {error ? <div className="notifications-error"><FaExclamationTriangle /><span>{error}</span><button type="button" onClick={loadNotifications}>Try again</button></div>
          : loading ? <div className="notifications-state">Loading notification history...</div>
            : filteredNotifications.length === 0 ? <div className="notifications-state"><FaBell /><h2>No {filter === "all" ? "notifications" : `${filter} notifications`}</h2><p>New Help Desk notifications will appear here.</p></div>
              : <div className="notifications-list">
                {filteredNotifications.map((notification) => {
                  const dateTime = formatDateTime(notification.created_at);
                  return <button type="button" key={notification.id} className={`notifications-row${notification.read ? "" : " unread"}`} onClick={() => openNotification(notification)} disabled={markingId === notification.id}>
                    <span className="notifications-icon" aria-hidden="true">{notification.read ? <FaCheckCircle /> : <FaBell />}</span>
                    <span className="notifications-content">
                      <span className="notifications-title-line"><strong>{notification.title}</strong><em>{notification.type || "Notification"}</em></span>
                      <span className="notifications-message">{notification.message}</span>
                    </span>
                    <time dateTime={notification.created_at}><span>{dateTime.date}</span><span>{dateTime.time}</span></time>
                  </button>;
                })}
              </div>}
        {!loading && !error && notifications.length === API_LIMIT && <p className="notifications-limit-note">Showing the latest {API_LIMIT} notifications returned by the current notification API. Older records are not exposed by its existing contract.</p>}
      </section>
    </main>
  );
}
